import type {
  EnvironmentDeployment,
  EvidenceArtifact,
  MergeReadinessItem,
  PortalRepository,
  PortalRequestDetail,
  PortalRequestSummary,
  PortalUser,
  RequestState,
} from "./portal-api";
import type { RequestStep } from "./portal-routing";

export const PORTAL_QA_CONFIG_PATH = ".netra/qa.toml";

export function canDeleteRequest(user: PortalUser | null, request: PortalRequestDetail): boolean {
  if (!user || !["merged", "closed", "cancelled"].includes(request.state)) return false;
  return user.role === "admin"
    || user.email.toLowerCase() === request.requester.email.toLowerCase();
}

export function canCancelRequest(user: PortalUser | null, request: PortalRequestDetail): boolean {
  return Boolean(user) && !["merged", "closed", "cancelled"].includes(request.state);
}

export function canRequestChanges(request: PortalRequestDetail): boolean {
  if (["awaiting_feedback", "merge_ready", "failed"].includes(request.state)) return true;
  return request.state === "verifying"
    && Boolean(request.github?.head_sha)
    && request.preview?.exact_head === true
    && request.preview.head_sha === request.github?.head_sha;
}

export function canCompleteInformationRequest(user: PortalUser | null, request: PortalRequestDetail): boolean {
  if (
    request.request_type !== "information"
    || ["merged", "closed", "cancelled"].includes(request.state)
    || !user
  ) return false;
  return user.role === "operator"
    || user.role === "admin"
    || user.email.toLowerCase() === request.requester.email.toLowerCase();
}

export function newPortalRepository(): PortalRepository {
  return {
    repository: "",
    enabled: false,
    qa_config_path: PORTAL_QA_CONFIG_PATH,
    secret_aliases: [],
    retention_days: 30,
    max_concurrency: 1,
  };
}

export function normalizePortalRepository(repository: PortalRepository): PortalRepository {
  return {
    ...repository,
    repository: repository.repository.trim(),
    qa_config_path: (repository.qa_config_path ?? PORTAL_QA_CONFIG_PATH).trim(),
    secret_aliases: [...new Set((repository.secret_aliases ?? []).map((alias) => alias.trim()).filter(Boolean))],
    retention_days: repository.retention_days ?? 30,
    max_concurrency: repository.max_concurrency ?? 1,
  };
}

/** Builds the safe toggle payload used by the unified repository list. */
export function portalRepositoryWithEnabled(
  repository: string,
  current: PortalRepository | undefined,
  enabled: boolean,
): PortalRepository {
  return normalizePortalRepository({
    ...newPortalRepository(),
    ...current,
    repository,
    enabled,
  });
}

export function validatePortalRepository(repository: PortalRepository): string | null {
  const value = normalizePortalRepository(repository);
  const parts = value.repository.split("/");
  if (
    parts.length !== 2
    || parts.some((part) => !part || part.length > 100)
    || parts.some((part) => !/^[A-Za-z0-9_.-]+$/.test(part))
  ) {
    return "Repository must use the GitHub owner/name format.";
  }
  if (value.qa_config_path !== PORTAL_QA_CONFIG_PATH) {
    return `QA scenarios must use ${PORTAL_QA_CONFIG_PATH}.`;
  }
  if (!Number.isInteger(value.retention_days) || value.retention_days! < 1 || value.retention_days! > 3650) {
    return "Artifact retention must be a whole number from 1 to 3650 days.";
  }
  if (!Number.isInteger(value.max_concurrency) || value.max_concurrency! < 1 || value.max_concurrency! > 32) {
    return "Concurrent workflows must be a whole number from 1 to 32.";
  }
  if (
    value.secret_aliases!.length > 100
    || value.secret_aliases!.some((alias) => alias.length > 100 || !/^[A-Za-z0-9_.-]+$/.test(alias))
  ) {
    return "Secret aliases may contain letters, numbers, dots, dashes, and underscores only.";
  }
  return null;
}

export const REQUEST_STATE_META: Record<RequestState, {
  label: string;
  group: "needs_action" | "in_progress" | "ready" | "complete";
  tone: "amber" | "blue" | "green" | "red" | "muted";
}> = {
  needs_input: { label: "Needs input", group: "needs_action", tone: "amber" },
  plan_approval: { label: "Plan approval", group: "needs_action", tone: "amber" },
  failed: { label: "Failed", group: "needs_action", tone: "red" },
  merge_blocked: { label: "Merge blocked", group: "needs_action", tone: "red" },
  changes_requested: { label: "Changes requested", group: "in_progress", tone: "blue" },
  queued: { label: "Queued", group: "in_progress", tone: "blue" },
  building: { label: "Building", group: "in_progress", tone: "blue" },
  previewing: { label: "Deploying preview", group: "in_progress", tone: "blue" },
  verifying: { label: "Verifying", group: "in_progress", tone: "blue" },
  awaiting_feedback: { label: "Awaiting feedback", group: "ready", tone: "green" },
  merge_ready: { label: "Merge ready", group: "ready", tone: "green" },
  merged: { label: "Merged", group: "complete", tone: "green" },
  closed: { label: "Closed", group: "complete", tone: "muted" },
  cancelled: { label: "Cancelled", group: "complete", tone: "muted" },
};

export type RequestGroupKey = "needs_action" | "in_progress" | "ready" | "complete";

export const REQUEST_GROUPS = [
  { key: "needs_action", label: "Needs your attention", description: "Questions, approvals, and blocked work" },
  { key: "in_progress", label: "In progress", description: "Netra is planning, building, or verifying" },
  { key: "ready", label: "Ready for you", description: "Review proof or merge in GitHub" },
  { key: "complete", label: "Completed", description: "Accepted, merged, closed, or cancelled" },
] as const;

/// Auto-mode requests never wait on anyone, so they are only ever in progress
/// or complete — a failed or cancelled run is finished, not "needs attention".
export function requestGroupKey(request: Pick<PortalRequestSummary, "mode" | "state">): RequestGroupKey {
  if (request.mode === "auto") {
    return ["closed", "failed", "cancelled", "merged"].includes(request.state) ? "complete" : "in_progress";
  }
  return REQUEST_STATE_META[request.state]?.group ?? "in_progress";
}

export function groupRequests(requests: PortalRequestSummary[]) {
  return REQUEST_GROUPS.map((group) => ({
    ...group,
    requests: requests.filter((request) => requestGroupKey(request) === group.key),
  }));
}

/// Columns that only guided requests can fill appear only when one does.
export function visibleRequestGroups<T extends { key: RequestGroupKey; requests: unknown[] }>(groups: T[], showComplete: boolean) {
  return groups.filter((group) => {
    if (group.key === "complete") return showComplete;
    if (group.key === "in_progress") return true;
    return group.requests.length > 0;
  });
}

export function isAutoRequestActive(request: Pick<PortalRequestSummary, "mode" | "state">) {
  return request.mode === "auto" && ["queued", "building"].includes(request.state);
}

/// One line that says where an auto-mode request stands.
export function autopilotHeadline(request: Pick<PortalRequestSummary, "state" | "autopilot">) {
  const pulls = request.autopilot?.pull_requests.length ?? 0;
  switch (request.state) {
    case "queued":
      return "Queued — Netra starts as soon as an agent is free.";
    case "building":
      return request.autopilot?.status === "finalizing"
        ? "Opening pull requests…"
        : "Working — finding the repositories and making the change.";
    case "closed":
      return pulls === 0 ? "Done — no code change was needed." : `Done — ${pulls} pull request${pulls === 1 ? "" : "s"} opened.`;
    case "failed":
      return pulls === 0
        ? "Netra could not finish this request."
        : `Netra could not finish — ${pulls} pull request${pulls === 1 ? " was" : "s were"} opened first.`;
    case "cancelled":
      return pulls === 0 ? "Cancelled." : `Stopped — ${pulls} pull request${pulls === 1 ? " was" : "s were"} opened first.`;
    default:
      return REQUEST_STATE_META[request.state]?.label ?? request.state;
  }
}

export function exactHeadEvidence(request: Pick<PortalRequestDetail, "github" | "preview" | "qa_run" | "artifacts">) {
  const expected = request.github?.head_sha ?? request.preview?.head_sha ?? request.qa_run?.head_sha;
  if (!expected) return { exact: false, headSha: undefined, reason: "The pull request head is not available yet." };
  if (!request.preview) {
    return { exact: false, headSha: expected, reason: "No preview has been published for this revision." };
  }
  if (!request.preview.exact_head || request.preview.head_sha !== expected) {
    return { exact: false, headSha: expected, reason: "The preview does not match the current pull request head." };
  }
  if (!request.qa_run) {
    return { exact: false, headSha: expected, reason: "QA has not completed for this revision." };
  }
  if (request.qa_run.head_sha !== expected) {
    return { exact: false, headSha: expected, reason: "The QA run is for an older pull request revision." };
  }
  if (request.qa_run.status !== "passed" || request.qa_run.failed > 0) {
    return { exact: false, headSha: expected, reason: "QA has not passed for the current pull request revision." };
  }
  const currentArtifacts = request.artifacts.filter((artifact) => artifact.head_sha === expected);
  if (currentArtifacts.length === 0) {
    return { exact: false, headSha: expected, reason: "No screenshots, recording, or report has been published for this revision." };
  }
  if (!currentArtifacts.some((artifact) => artifact.kind === "video")) {
    return { exact: false, headSha: expected, reason: "A short recording of the fixed behavior has not been published for this revision." };
  }
  return {
    exact: true,
    headSha: expected,
    reason: "The preview, QA, and fix recording are tied to the current pull request head.",
  };
}

export function canApprovePlan(request: PortalRequestDetail, selectedVersion: number) {
  const plan = request.plans.find((candidate) => candidate.version === selectedVersion);
  return !request.intake_analysis_pending
    && request.state === "plan_approval"
    && ["pending", "pending_approval"].includes(plan?.status ?? "");
}

export function canAcceptRequest(request: PortalRequestDetail) {
  const head = request.github?.head_sha;
  const exactPreview = Boolean(
    head
    && request.preview?.exact_head
    && request.preview.head_sha === head,
  );
  const alreadyApproved = Boolean(
    head && request.requester_review?.head_sha === head,
  );
  return ["verifying", "awaiting_feedback", "failed"].includes(request.state)
    && exactPreview
    && !alreadyApproved;
}

export function requesterApprovedCurrentRevision(request: PortalRequestDetail) {
  const head = request.github?.head_sha;
  return Boolean(head && request.requester_review?.head_sha === head);
}

export const REQUEST_STEP_META: Array<{
  key: RequestStep;
  label: string;
  description: string;
}> = [
  { key: "request", label: "Request", description: "Problem, conversation, and acceptance checks" },
  { key: "plan", label: "Plan", description: "Review and approve the implementation plan" },
  { key: "delivery", label: "Delivery", description: "Follow the issue, pull request, and preview" },
  { key: "qa", label: "QA", description: "Try the exact preview and record your decision" },
  { key: "merge", label: "Merge", description: "Complete the final human merge in GitHub" },
  { key: "deploy", label: "Deploy", description: "Confirm the merged revision reached its environment" },
];

export function currentRequestStep(request: PortalRequestDetail): RequestStep {
  if (request.state === "merged" || (request.state === "closed" && request.deployment)) return "deploy";
  if (["merge_ready", "merge_blocked", "closed"].includes(request.state)) return "merge";
  if (
    ["verifying", "awaiting_feedback", "failed"].includes(request.state)
    && Boolean(request.preview || request.qa_run || request.artifacts.length)
  ) return "qa";
  if (["queued", "building", "previewing", "verifying", "failed"].includes(request.state)) return "delivery";
  if (["plan_approval", "changes_requested"].includes(request.state) || request.plans.length > 0) return "plan";
  return "request";
}

export function furthestRequestStep(request: PortalRequestDetail): RequestStep {
  if (request.state === "merged" || (request.state === "closed" && request.deployment)) return "deploy";
  if (["merge_ready", "merge_blocked", "closed"].includes(request.state)) return "merge";
  // Once the requester has approved this exact revision their part of QA is
  // done, so the merge step opens even while automated gates are still running.
  if (requesterApprovedCurrentRevision(request) && request.github?.pull_request_number) return "merge";
  if (request.preview?.exact_head || request.qa_run || request.artifacts.length > 0) return "qa";
  if (["queued", "building", "previewing", "verifying", "awaiting_feedback", "failed"].includes(request.state) || request.github?.issue_number || request.cycles.length > 0) return "delivery";
  if (request.plans.length > 0) return "plan";
  return "request";
}

export function requestStepAvailable(request: PortalRequestDetail, step: RequestStep) {
  const requested = REQUEST_STEP_META.findIndex((item) => item.key === step);
  const furthest = REQUEST_STEP_META.findIndex((item) => item.key === furthestRequestStep(request));
  return requested <= furthest;
}

/// The gates that still stand between a revision and its merge, in the order a
/// person meets them. An empty list means the pull request is ready to merge.
export function outstandingMergeGates(request: PortalRequestDetail): string[] {
  const head = request.github?.head_sha;
  const gates: string[] = [];
  if (!head) gates.push("A pull request head has not been recorded yet.");
  if (!request.preview?.exact_head || (head && request.preview?.head_sha !== head)) {
    gates.push("A preview of this exact head is still being published.");
  }
  if (!request.qa_run || request.qa_run.head_sha !== head) {
    gates.push("Automated QA has not finished for this head.");
  } else if (request.qa_run.status !== "passed" || request.qa_run.failed > 0) {
    gates.push("Automated QA has not passed for this head.");
  }
  if (!request.artifacts.some((artifact) => artifact.kind === "video" && artifact.head_sha === head)) {
    gates.push("The recording of the fixed behaviour has not been published yet.");
  }
  if (!requesterApprovedCurrentRevision(request)) {
    gates.push("Your Looks good decision has not been recorded against this head.");
  }
  if (request.github?.mergeable_state === "CONFLICTING") {
    gates.push("The pull request conflicts with its base branch.");
  }
  return gates;
}

export const DEPLOYMENT_TIERS = [
  { tier: "test", label: "Test" },
  { tier: "production", label: "Prod" },
] as const;

/// The recorded state of one environment tier, or undefined when Netra has not
/// observed that tier at all.
export function environmentTier(
  deployments: EnvironmentDeployment[] | undefined,
  tier: string,
): EnvironmentDeployment | undefined {
  return (deployments ?? []).find((deployment) => deployment.tier === tier);
}

/// Whether a tier has the change, by provider confirmation or branch containment.
export function deploymentReached(deployment?: EnvironmentDeployment): boolean {
  return ["success", "merged"].includes(deployment?.status ?? "");
}

/// One short label per tier for the merge desk and the deploy step.
export function deploymentTierSummary(deployment: EnvironmentDeployment | undefined, label: string) {
  if (!deployment) {
    return { mark: "·", tone: "pending", text: `${label} not tracked`, detail: `Netra has not observed a ${label.toLowerCase()} environment for this repository.` };
  }
  if (deployment.status === "success") {
    return { mark: "✓", tone: "good", text: `${label} live`, detail: deployment.detail ?? `Deployed to ${deployment.environment}.` };
  }
  if (deployment.status === "merged") {
    return { mark: "✓", tone: "reached", text: `In ${label.toLowerCase()}`, detail: deployment.detail ?? `The revision is in ${deployment.environment}.` };
  }
  if (deployment.status === "failed") {
    return { mark: "×", tone: "bad", text: `${label} failed`, detail: deployment.detail ?? `The ${label.toLowerCase()} deployment failed.` };
  }
  if (deployment.status === "running") {
    return { mark: "…", tone: "pending", text: `${label} deploying`, detail: deployment.detail ?? `Deploying to ${deployment.environment}.` };
  }
  return { mark: "·", tone: "pending", text: `${label} pending`, detail: deployment.detail ?? `The revision has not reached ${deployment.environment} yet.` };
}

/// Whether this person may merge a managed pull request from Netra.
///
/// Merging stays a human decision, and an irreversible one, so it is limited to
/// an admin and to a row whose exact-head gates and requester approval all hold.
export function canMergeFromPortal(user: PortalUser | null, item: MergeReadinessItem): boolean {
  return user?.role === "admin"
    && item.state === "ready"
    && Boolean(item.mergeable && item.exact_head_verified && item.pull_request_number)
    && item.requester_approved_head_sha === item.head_sha;
}

export const WORKFLOW_STAGE_GUIDANCE: Record<RequestState, { title: string; body: string }> = {
  needs_input: {
    title: "Tell Netra what happened",
    body: "Answer one question at a time. Netra will turn your answers into clear requirements and acceptance checks.",
  },
  plan_approval: {
    title: "Review the implementation plan",
    body: "Nothing is built until you approve this exact plan. Ask for changes if the scope or expected result is wrong.",
  },
  changes_requested: {
    title: "Preparing a revised plan",
    body: "Netra is incorporating your feedback. The revised plan must be approved before delivery continues.",
  },
  queued: {
    title: "Approved and queued",
    body: "Your approval created durable delivery work. Netra will begin the fix as soon as a worker is available.",
  },
  building: {
    title: "Reproducing, then fixing the issue",
    body: "Netra must prove the reported failure before editing code. Only then will it implement the approved fix and open or update one managed pull request.",
  },
  previewing: {
    title: "Deploying the pull request",
    body: "Netra is publishing an isolated preview tied to the current pull-request head.",
  },
  verifying: {
    title: "Running verification",
    body: "CI, repository QA, exploratory checks, and a short fix recording must all pass against the same exact head.",
  },
  awaiting_feedback: {
    title: "Review the working revision",
    body: "Watch the fix recording and open the preview dashboard yourself. Accept the revision or describe what still needs to change.",
  },
  merge_ready: {
    title: "Ready for human merge",
    body: "You accepted the verified revision. Open the pull request in GitHub for the final human merge.",
  },
  merge_blocked: {
    title: "Merge needs attention",
    body: "A merge gate or conflict is blocking the pull request. An operator can retry after the cause is resolved.",
  },
  failed: {
    title: "This stage needs attention",
    body: "Netra stopped after an error or abort. Retry the stage, request a revised plan, or cancel the request.",
  },
  merged: {
    title: "Merged",
    body: "The verified pull request has been merged. The request and its evidence remain available for reference.",
  },
  closed: {
    title: "Request complete",
    body: "The request is closed and its conversation and evidence are retained.",
  },
  cancelled: {
    title: "Request cancelled",
    body: "Active Netra work was stopped. You can keep this record for reference or delete it.",
  },
};

export function artifactsByKind(artifacts: EvidenceArtifact[]) {
  return {
    videos: artifacts.filter((artifact) => artifact.kind === "video"),
    screenshots: artifacts.filter((artifact) => artifact.kind === "screenshot"),
    reports: artifacts.filter((artifact) => !["video", "screenshot"].includes(artifact.kind)),
  };
}

export function compactSha(sha?: string) {
  return sha ? sha.slice(0, 8) : "pending";
}

export function planLineDiff(current: string, previous?: string) {
  if (previous === undefined) return current.split("\n").map((line) => `+ ${line}`).join("\n");
  if (current === previous) return "No textual changes from the previous plan.";
  const before = previous.split("\n");
  const after = current.split("\n");
  let prefix = 0;
  while (prefix < before.length && prefix < after.length && before[prefix] === after[prefix]) prefix += 1;
  let suffix = 0;
  while (
    suffix < before.length - prefix
    && suffix < after.length - prefix
    && before[before.length - 1 - suffix] === after[after.length - 1 - suffix]
  ) suffix += 1;
  return [
    ...before.slice(0, prefix).map((line) => `  ${line}`),
    ...before.slice(prefix, before.length - suffix).map((line) => `- ${line}`),
    ...after.slice(prefix, after.length - suffix).map((line) => `+ ${line}`),
    ...after.slice(after.length - suffix).map((line) => `  ${line}`),
  ].join("\n");
}

export function formatRelativeTime(value: string, now = Date.now()) {
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return "unknown";
  const seconds = Math.round((timestamp - now) / 1000);
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  if (Math.abs(seconds) < 60) return formatter.format(seconds, "second");
  const minutes = Math.round(seconds / 60);
  if (Math.abs(minutes) < 60) return formatter.format(minutes, "minute");
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return formatter.format(hours, "hour");
  return formatter.format(Math.round(hours / 24), "day");
}
